import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Radio,
  RadioGroup,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';
import { authGet, authPost, authPut, authDelete } from '../../services/apiService';

const darkGreenColor = '#1D4434';

const ENVIRONMENT_OPTIONS = ['PROD', 'UAT', 'Non-PROD', 'Lab'];

const ENV_CHIP_COLOR = {
  PROD: 'error',
  UAT: 'warning',
  'Non-PROD': 'info',
  Lab: 'default'
};

const EMPTY_URL_FORM = {
  name: '',
  url: '',
  region: '',
  environment: 'PROD',
  active: true
};

const EMPTY_SCHEDULER = {
  enabled: false,
  mode: 'interval',
  intervalHours: 6,
  hour: 8,
  minute: 0,
  teamsWebhookUrl: '',
};

const SyntheticTestsAdmin = () => {
  // ── Registered URLs ──────────────────────────────────────────────────────────
  const [configs, setConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' | 'edit'
  const [formData, setFormData] = useState(EMPTY_URL_FORM);
  const [editingId, setEditingId] = useState(null);
  const [savingForm, setSavingForm] = useState(false);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingName, setDeletingName] = useState('');
  const [deletingInProgress, setDeletingInProgress] = useState(false);

  // ── Scheduler ────────────────────────────────────────────────────────────────
  const [scheduler, setScheduler] = useState(EMPTY_SCHEDULER);
  const [loadingScheduler, setLoadingScheduler] = useState(true);
  const [savingScheduler, setSavingScheduler] = useState(false);
  const [schedulerLastRun, setSchedulerLastRun] = useState(null);

  // ── Feedback alerts ──────────────────────────────────────────────────────────
  const [urlsAlert, setUrlsAlert] = useState(null);   // { severity, message }
  const [schedAlert, setSchedAlert] = useState(null); // { severity, message }

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const showUrlsAlert = (severity, message) => {
    setUrlsAlert({ severity, message });
    setTimeout(() => setUrlsAlert(null), 4000);
  };

  const showSchedAlert = (severity, message) => {
    setSchedAlert({ severity, message });
    setTimeout(() => setSchedAlert(null), 4000);
  };

  // ── Data fetching ────────────────────────────────────────────────────────────
  const fetchConfigs = async () => {
    try {
      setLoadingConfigs(true);
      const response = await authGet('/api/synthetic-tests/configs');
      if (response.success) {
        setConfigs(response.data || []);
      } else {
        showUrlsAlert('error', response.message || 'Failed to load test URL configurations.');
      }
    } catch (err) {
      console.error('Error fetching synthetic test configs:', err);
      showUrlsAlert('error', err.response?.data?.message || 'Failed to load test URL configurations.');
    } finally {
      setLoadingConfigs(false);
    }
  };

  const fetchScheduler = async () => {
    try {
      setLoadingScheduler(true);
      const response = await authGet('/api/synthetic-tests/scheduler');
      if (response.success && response.data) {
        const d = response.data;
        setScheduler({
          enabled: d.isEnabled ?? d.enabled ?? false,
          mode: d.mode || 'interval',
          intervalHours: d.intervalHours ?? 6,
          hour: d.specificTime?.hour ?? d.hour ?? 8,
          minute: d.specificTime?.minute ?? d.minute ?? 0,
          teamsWebhookUrl: d.teamsWebhookUrl ?? '',
        });
        setSchedulerLastRun(d.lastRun || null);
      } else {
        showSchedAlert('error', response.message || 'Failed to load scheduler configuration.');
      }
    } catch (err) {
      console.error('Error fetching synthetic test scheduler:', err);
      showSchedAlert('error', err.response?.data?.message || 'Failed to load scheduler configuration.');
    } finally {
      setLoadingScheduler(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
    fetchScheduler();
  }, []);

  // ── URL form handlers ────────────────────────────────────────────────────────
  const openAddDialog = () => {
    setFormData(EMPTY_URL_FORM);
    setEditingId(null);
    setFormMode('add');
    setFormOpen(true);
  };

  const openEditDialog = (config) => {
    setFormData({
      name: config.name || '',
      url: config.url || '',
      region: config.region || '',
      environment: config.environment || 'PROD',
      active: config.active !== false
    });
    setEditingId(config._id || config.id);
    setFormMode('edit');
    setFormOpen(true);
  };

  const handleFormClose = () => {
    if (savingForm) return;
    setFormOpen(false);
  };

  const handleFormSave = async () => {
    if (!formData.name.trim() || !formData.url.trim()) return;
    try {
      setSavingForm(true);
      let response;
      if (formMode === 'add') {
        response = await authPost('/api/synthetic-tests/configs', formData);
      } else {
        response = await authPut(`/api/synthetic-tests/configs/${editingId}`, formData);
      }
      if (response.success) {
        setFormOpen(false);
        showUrlsAlert('success', formMode === 'add' ? 'Test URL added successfully.' : 'Test URL updated successfully.');
        await fetchConfigs();
      } else {
        showUrlsAlert('error', response.message || 'Failed to save test URL.');
      }
    } catch (err) {
      console.error('Error saving synthetic test config:', err);
      showUrlsAlert('error', err.response?.data?.message || 'Failed to save test URL.');
    } finally {
      setSavingForm(false);
    }
  };

  // ── Delete handlers ──────────────────────────────────────────────────────────
  const openDeleteDialog = (config) => {
    setDeletingId(config._id || config.id);
    setDeletingName(config.name || config.url);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeletingInProgress(true);
      const response = await authDelete(`/api/synthetic-tests/configs/${deletingId}`);
      if (response.success) {
        setDeleteDialogOpen(false);
        showUrlsAlert('success', `"${deletingName}" deleted successfully.`);
        await fetchConfigs();
      } else {
        showUrlsAlert('error', response.message || 'Failed to delete test URL.');
        setDeleteDialogOpen(false);
      }
    } catch (err) {
      console.error('Error deleting synthetic test config:', err);
      showUrlsAlert('error', err.response?.data?.message || 'Failed to delete test URL.');
      setDeleteDialogOpen(false);
    } finally {
      setDeletingInProgress(false);
    }
  };

  // ── Scheduler handlers ───────────────────────────────────────────────────────
  const handleSchedulerSave = async () => {
    try {
      setSavingScheduler(true);
      const payload = {
        isEnabled: scheduler.enabled,
        mode: scheduler.mode,
        intervalHours: scheduler.intervalHours,
        specificTime: { hour: scheduler.hour, minute: scheduler.minute },
        teamsWebhookUrl: scheduler.teamsWebhookUrl,
      };
      const response = await authPut('/api/synthetic-tests/scheduler', payload);
      if (response.success) {
        showSchedAlert('success', 'Scheduler configuration saved successfully.');
      } else {
        showSchedAlert('error', response.message || 'Failed to save scheduler configuration.');
      }
    } catch (err) {
      console.error('Error saving synthetic test scheduler:', err);
      showSchedAlert('error', err.response?.data?.message || 'Failed to save scheduler configuration.');
    } finally {
      setSavingScheduler(false);
    }
  };

  const handleSchedulerChange = (field, value) => {
    setScheduler((prev) => ({ ...prev, [field]: value }));
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: darkGreenColor }}>
        Synthetic Tests
      </Typography>

      {/* ── Section 1: Registered Test URLs ─────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ color: darkGreenColor }}>
            Registered Test URLs
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            sx={{ bgcolor: darkGreenColor, '&:hover': { bgcolor: '#2d5a47' } }}
          >
            Add New
          </Button>
        </Box>

        {urlsAlert && (
          <Alert severity={urlsAlert.severity} sx={{ mb: 2 }} onClose={() => setUrlsAlert(null)}>
            {urlsAlert.message}
          </Alert>
        )}

        {loadingConfigs ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={36} sx={{ color: darkGreenColor }} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, color: darkGreenColor } }}>
                  <TableCell>Name</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No test URLs registered. Click "Add New" to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  configs.map((config) => (
                    <TableRow key={config._id || config.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{config.name}</TableCell>
                      <TableCell sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={config.url} placement="top">
                          <span>{config.url}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{config.region || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={config.environment || 'PROD'}
                          color={ENV_CHIP_COLOR[config.environment] || 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {config.active !== false ? (
                          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <CancelIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(config)} sx={{ color: darkGreenColor }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => openDeleteDialog(config)} sx={{ color: 'error.main' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ── Section 2: Scheduler Configuration ──────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <ScheduleIcon sx={{ mr: 1, color: darkGreenColor }} />
          <Typography variant="h6" sx={{ color: darkGreenColor }}>
            Scheduler Configuration
          </Typography>
        </Box>

        {schedAlert && (
          <Alert severity={schedAlert.severity} sx={{ mb: 2 }} onClose={() => setSchedAlert(null)}>
            {schedAlert.message}
          </Alert>
        )}

        {loadingScheduler ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={36} sx={{ color: darkGreenColor }} />
          </Box>
        ) : (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={scheduler.enabled}
                  onChange={(e) => handleSchedulerChange('enabled', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: darkGreenColor },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: darkGreenColor }
                  }}
                />
              }
              label={
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Enable Scheduler
                </Typography>
              }
              sx={{ mb: 2 }}
            />

            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
              Schedule Mode
            </Typography>
            <RadioGroup
              row
              value={scheduler.mode}
              onChange={(e) => handleSchedulerChange('mode', e.target.value)}
              sx={{ mb: 2 }}
            >
              <FormControlLabel
                value="interval"
                control={<Radio sx={{ '&.Mui-checked': { color: darkGreenColor } }} />}
                label="Every X Hours"
              />
              <FormControlLabel
                value="specific"
                control={<Radio sx={{ '&.Mui-checked': { color: darkGreenColor } }} />}
                label="At Specific Time"
              />
            </RadioGroup>

            {scheduler.mode === 'interval' ? (
              <TextField
                label="Run every X hours"
                type="number"
                value={scheduler.intervalHours}
                onChange={(e) => {
                  const val = Math.min(24, Math.max(1, parseInt(e.target.value) || 1));
                  handleSchedulerChange('intervalHours', val);
                }}
                inputProps={{ min: 1, max: 24, step: 1 }}
                helperText="Enter a value between 1 and 24"
                sx={{ width: 220 }}
              />
            ) : (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  label="Hour (0–23)"
                  type="number"
                  value={scheduler.hour}
                  onChange={(e) => {
                    const val = Math.min(23, Math.max(0, parseInt(e.target.value) || 0));
                    handleSchedulerChange('hour', val);
                  }}
                  inputProps={{ min: 0, max: 23, step: 1 }}
                  helperText="UTC hour (0–23)"
                  sx={{ width: 160 }}
                />
                <TextField
                  label="Minute (0–59)"
                  type="number"
                  value={scheduler.minute}
                  onChange={(e) => {
                    const val = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                    handleSchedulerChange('minute', val);
                  }}
                  inputProps={{ min: 0, max: 59, step: 1 }}
                  helperText="UTC minute (0–59)"
                  sx={{ width: 160 }}
                />
              </Box>
            )}

            {schedulerLastRun && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Last run: {new Date(schedulerLastRun).toLocaleString()}
              </Typography>
            )}

            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                startIcon={savingScheduler ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSchedulerSave}
                disabled={savingScheduler}
                sx={{ bgcolor: darkGreenColor, '&:hover': { bgcolor: '#2d5a47' } }}
              >
                {savingScheduler ? 'Saving...' : 'Save Scheduler'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* ── Section 3: Notifications ────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <NotificationsIcon sx={{ mr: 1, color: darkGreenColor }} />
          <Typography variant="h6" sx={{ color: darkGreenColor }}>
            Notifications
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When a test run completes with errors or timeouts, a summary card will be posted to the
          Microsoft Teams channel associated with this webhook URL.
        </Typography>

        <TextField
          label="Teams Incoming Webhook URL"
          value={scheduler.teamsWebhookUrl}
          onChange={(e) => handleSchedulerChange('teamsWebhookUrl', e.target.value)}
          fullWidth
          placeholder="https://outlook.office.com/webhook/..."
          helperText="Leave blank to disable Teams notifications."
          disabled={loadingScheduler}
        />

        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={savingScheduler ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSchedulerSave}
            disabled={savingScheduler || loadingScheduler}
            sx={{ bgcolor: darkGreenColor, '&:hover': { bgcolor: '#2d5a47' } }}
          >
            {savingScheduler ? 'Saving...' : 'Save Notifications'}
          </Button>
        </Box>
      </Paper>

      {/* ── Add / Edit Dialog ────────────────────────────────────────────────── */}
      <Dialog open={formOpen} onClose={handleFormClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: darkGreenColor }}>
          {formMode === 'add' ? 'Add New Test URL' : 'Edit Test URL'}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              autoFocus
              disabled={savingForm}
            />
            <TextField
              label="URL"
              value={formData.url}
              onChange={(e) => setFormData((prev) => ({ ...prev, url: e.target.value }))}
              fullWidth
              required
              placeholder="https://example.com/health"
              disabled={savingForm}
            />
            <TextField
              label="Region"
              value={formData.region}
              onChange={(e) => setFormData((prev) => ({ ...prev, region: e.target.value }))}
              fullWidth
              placeholder="e.g. us-east-1"
              disabled={savingForm}
            />
            <FormControl fullWidth disabled={savingForm}>
              <InputLabel>Environment</InputLabel>
              <Select
                value={formData.environment}
                label="Environment"
                onChange={(e) => setFormData((prev) => ({ ...prev, environment: e.target.value }))}
              >
                {ENVIRONMENT_OPTIONS.map((env) => (
                  <MenuItem key={env} value={env}>
                    <Chip label={env} color={ENV_CHIP_COLOR[env] || 'default'} size="small" sx={{ mr: 1 }} />
                    {env}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                  disabled={savingForm}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: darkGreenColor },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: darkGreenColor }
                  }}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleFormClose} disabled={savingForm} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFormSave}
            disabled={savingForm || !formData.name.trim() || !formData.url.trim()}
            startIcon={savingForm ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ bgcolor: darkGreenColor, '&:hover': { bgcolor: '#2d5a47' } }}
          >
            {savingForm ? 'Saving...' : formMode === 'add' ? 'Add' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ───────────────────────────────────────── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletingInProgress && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: '#d32f2f' }}>Delete Test URL</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deletingName}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deletingInProgress}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deletingInProgress}
            startIcon={deletingInProgress ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {deletingInProgress ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SyntheticTestsAdmin;
